/*
 * ErrorLogViewToolbar.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";
import { useDeepCompareCallback } from "use-deep-compare";
import { ErrorLogView } from "../ErrorLogView";

export interface IErrorLogViewToolbarProps {
	view: ErrorLogView;
	className?: string;
	severityFilter?: Set<DOC_ERROR_SEVERITY>;
	onFilterClick?: (severity: DOC_ERROR_SEVERITY) => void;
}

export const ErrorLogViewErrorsToolbar: React.FC<IErrorLogViewToolbarProps> = ({
	className,
	severityFilter,
	onFilterClick,
	view
}) => {
	const [ refreshInProgress, setRefreshInProgress ] = useState(false);
	const baseClass = BEM("edt-error-log-view-errors-toolbar");
	const buttonClass = BEM(baseClass.toClassName(), "button");

	const getOnFilterClick = useCallback<(severity: DOC_ERROR_SEVERITY) => () => void>(
		(severity) => () => {
			if (isFunction(onFilterClick)) {
				onFilterClick(severity);
			}
		},
		[ onFilterClick ]
	);

	const isActive = useDeepCompareCallback<(severity: DOC_ERROR_SEVERITY) => boolean>(
		(severity) => severityFilter?.has(severity),
		[ severityFilter ]
	);

	const reloadErrors = useCallback(async () => {
		setRefreshInProgress(true);
		try {
			await view.reloadErrorList();
		} finally {
			setRefreshInProgress(false);
		}
	}, [ view ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<>
			<ToolbarButton
				icon="mdi/alert-circle"
				className={buttonClass.mod({ severity: "error" }).toClassName()}
				label={`Errors`}
				active={isActive(DOC_ERROR_SEVERITY.ERROR)}
				onClick={getOnFilterClick(DOC_ERROR_SEVERITY.ERROR)}
			/>
			<ToolbarButton
				icon="mdi/alert"
				className={buttonClass.mod({ severity: "warning" }).toClassName()}
				label={`Warnings`}
				active={isActive(DOC_ERROR_SEVERITY.WARNING)}
				onClick={getOnFilterClick(DOC_ERROR_SEVERITY.WARNING)}
			/>
			<ToolbarButton
				icon="mdi/lightbulb"
				className={buttonClass.mod({ severity: "hint" }).toClassName()}
				label={`Hints`}
				active={isActive(DOC_ERROR_SEVERITY.HINT)}
				onClick={getOnFilterClick(DOC_ERROR_SEVERITY.HINT)}
			/>
			<ToolbarButton
				icon="mdi/information"
				className={buttonClass.mod({ severity: "info" }).toClassName()}
				label={`Info`}
				active={isActive(DOC_ERROR_SEVERITY.INFO)}
				onClick={getOnFilterClick(DOC_ERROR_SEVERITY.INFO)}
			/>
			<ToolbarSpacer grow={1} />
			<ToolbarButton icon="mdi/refresh" onClick={reloadErrors} isLoading={refreshInProgress} />
		</>
	);
};
